import React, { useMemo } from 'react';
import { Button, Modal, TableProps } from 'antd';
import TableComponent from '../table';
import { IStock } from '@/interfaces/stock';
import { IStockReserved, IStockReservedItem } from '@/interfaces/reserved';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getStockReservedsByStockId } from '@/adapters/reserved';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { isFullAccess } from '@/utils/permissionUtils';

interface IProps {
  stock: IStock;
  open: boolean;
  handleCancel: () => void;
}

interface IReservedRow extends IStockReserved {
  itemQuantity: number;
  itemTake: number;
}

const getItemForStock = (
  reserved: IStockReserved,
  stock: IStock,
): Partial<IStockReservedItem> | undefined => {
  const items = reserved.stockReservedItems || [];
  return (
    items.find(i => Number(i.stockId) === Number(stock.id)) ||
    items.find(
      i =>
        !!i.stockCode &&
        i.stockCode.toLowerCase() === (stock.code || '').toLowerCase(),
    ) ||
    items[0]
  );
};

const StockReservedModal = ({ open, handleCancel, stock }: IProps) => {
  const navigate = useNavigate();
  const perFullAccess = isFullAccess();

  const { data, isLoading } = useQuery<AxiosResponse<IStockReserved[]>, Error>({
    queryKey: ['stock-reserved-by-stock', stock.id],
    queryFn: async () => await getStockReservedsByStockId(stock.id),
    enabled: open && !!stock.id,
  });

  const rows: IReservedRow[] = useMemo(() => {
    return (data?.data || []).map(rec => {
      const item = getItemForStock(rec, stock);
      return {
        ...rec,
        itemQuantity: Number(item?.quantity || 0),
        itemTake: Number(item?.take || 0),
      };
    });
  }, [data?.data, stock]);

  const columns: TableProps<IReservedRow>['columns'] = [
    {
      title: 'วันที่จอง',
      dataIndex: 'createDate',
      key: 'createDate',
      width: 110,
      align: 'center',
      render: _ => formatDate(_),
    },
    {
      title: 'รหัสลูกค้า',
      dataIndex: 'customerCode',
      key: 'customerCode',
      width: 100,
      className: 'font-bold',
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
    },
    {
      title: 'IV No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      width: 110,
      align: 'center',
    },
    {
      title: 'PO No.',
      dataIndex: 'poNo',
      key: 'poNo',
      width: 120,
      align: 'center',
    },
    {
      title: 'จอง',
      dataIndex: 'itemQuantity',
      key: 'itemQuantity',
      width: 100,
      align: 'right',
      className: 'font-bold text-orange-600',
      render: _ => formatNumber(_ || 0),
    },
    {
      title: 'ส่งแล้ว',
      dataIndex: 'itemTake',
      key: 'itemTake',
      width: 100,
      align: 'right',
      className: 'font-bold text-blue-600',
      render: _ => formatNumber(_ || 0),
    },
    {
      title: '',
      key: 'action',
      width: 70,
      align: 'center',
      render: (_, rec) => (
        <Button
          type="link"
          size="small"
          className="font-bold"
          onClick={() => {
            handleCancel();
            navigate(`/reserved/${rec.id}`);
          }}
        >
          ดู
        </Button>
      ),
    },
  ];

  const onAddReserved = () => {
    handleCancel();
    navigate('/reserved/create', { state: { stock } });
  };

  return (
    <Modal
      title={`รายการจอง — LOT ${stock.code}`}
      open={open}
      width={1000}
      onCancel={handleCancel}
      footer={[
        <Button key="close" onClick={handleCancel}>
          ปิด
        </Button>,
        perFullAccess && (
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddReserved}
          >
            เพิ่มรายการจอง
          </Button>
        ),
      ]}
    >
      <div className="flex flex-col gap-3 w-full">
        <div className="text-sm text-gray-600">
          {stock.productCode} — {stock.productName}
          <span className="ml-3 font-bold text-orange-600">
            จองรวม {formatNumber(stock.reservedQuantity || 0)}
          </span>
        </div>
        <TableComponent<IReservedRow>
          columns={columns}
          criteria={{}}
          data={{
            data: rows,
            totalRecords: rows.length,
          }}
          loading={isLoading}
          onTableChange={() => undefined}
          showPagination={false}
          scrollLength={900}
        />
      </div>
    </Modal>
  );
};

export default StockReservedModal;
